import type { User } from '@src/domain/users/entities/user.entity';
import type { Role } from '@src/domain/users/entities/role.entity';
import type { AuthTokens } from '@src/domain/users/value_objects';
import type { SendOtpResult, VerifyOtpResult, OnboardingResult, WhatsAppLinkCodeResult } from '@src/domain/users/repository';
import type {
    UserResponseDTO,
    RoleResponseDTO,
    AuthTokensResponseDTO,
    SendOtpResponseDTO,
    VerifyOtpResponseDTO,
    OnboardingResponseDTO,
    ProfileStateType,
    WhatsAppLinkCodeResponseDTO,
} from '../dto/auth-response.dto';

/**
 * Auth Response Mapper
 * Maps domain entities/value objects to application-layer DTOs for presentation
 * 
 * This mapper is the bridge between domain and presentation layers.
 * Presentation layer should ONLY use types from the application layer.
 */
export class AuthResponseMapper {
    /**
     * Convert Role domain entity to RoleResponseDTO
     */
    static roleToDTO(role: Role): RoleResponseDTO {
        return {
            roleId: role.roleId,
            name: role.name,
            displayName: role.displayName,
            description: role.description,
            isActive: role.isActive,
            permissions: role.permissions,
            createdAt: role.createdAt,
            updatedAt: role.updatedAt,
        };
    }

    /**
     * Convert User domain entity to UserResponseDTO
     */
    static userToDTO(user: User): UserResponseDTO {
        return {
            userId: user.userId,
            email: user.email,
            firstName: user.firstName,
            middleName: user.middleName,
            lastName: user.lastName,
            displayName: user.displayName,
            fullName: user.fullName,
            profilePictureUrl: user.profilePictureUrl,
            phone: user.phone,
            isActive: user.isActive,
            isVerified: user.isVerified,
            isBanned: user.isBanned,
            createdAt: user.createdAt,
            updatedAt: user.updatedAt,
            universityId: user.universityId,
            roles: user.roles.map(role => this.roleToDTO(role)),
            permissions: user.permissions,
            profileState: user.profileState as ProfileStateType,
            whatsappLinked: user.whatsappLinked,
        };
    }

    /**
     * Convert AuthTokens value object to AuthTokensResponseDTO
     */
    static tokensToDTO(tokens: AuthTokens): AuthTokensResponseDTO {
        return {
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            tokenType: tokens.tokenType,
        };
    }

    /**
     * Convert SendOtpResult domain type to SendOtpResponseDTO
     */
    static sendOtpResultToDTO(result: SendOtpResult): SendOtpResponseDTO {
        return {
            cooldownSeconds: result.cooldownSeconds,
            email: result.email,
            expiresInMinutes: result.expiresInMinutes,
            message: result.message,
        };
    }

    /**
     * Convert VerifyOtpResult domain type to VerifyOtpResponseDTO
     */
    static verifyOtpResultToDTO(result: VerifyOtpResult): VerifyOtpResponseDTO {
        return {
            email: result.email,
            isNewUser: result.isNewUser,
            profileState: result.profileState as ProfileStateType,
            tokens: this.tokensToDTO(result.tokens),
            userId: result.userId,
            whatsappLinked: result.whatsappLinked,
        };
    }

    /**
     * Convert OnboardingResult domain type to OnboardingResponseDTO
     */
    static onboardingResultToDTO(result: OnboardingResult): OnboardingResponseDTO {
        return {
            user: this.userToDTO(result.user),
        };
    }

    /**
     * Convert WhatsAppLinkCodeResult domain type to WhatsAppLinkCodeResponseDTO
     */
    static whatsAppLinkCodeResultToDTO(result: WhatsAppLinkCodeResult): WhatsAppLinkCodeResponseDTO {
        return {
            linkCode: result.linkCode,
            expiresInSeconds: result.expiresInSeconds,
            message: result.message,
        };
    }
}
